import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { CalendarDays, BookOpen, ChefHat, Flag, LineChart, ClipboardList, Sparkles, Users } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import BottomNav from "@/components/BottomNav";

// Module-level cache so every page that mounts PrimaryNav doesn't re-hit
// /plans. Only used to decide whether the Race tab exists.
let raceCache = null;

const hasRace = (plans = []) => plans.some((p) => p?.raceWeek?.enabled
  || (p?.trainingWeek?.days || []).some((d) => (d.sessions || []).some(
    (s) => s.isRace || (s.intensity || "").toLowerCase() === "race")));

// One shared nav bar for every logged-in page. Single scrollable row, active
// tab marked with a yellow underline; colour only as an icon accent.
export default function PrimaryNav({ plans, right }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { planTier, hasTracker, isCoach } = useAuth();
  const [race, setRace] = useState(plans ? hasRace(plans) : raceCache);

  useEffect(() => {
    if (plans) { const r = hasRace(plans); raceCache = r; setRace(r); return; }
    if (raceCache !== null) setRace(raceCache);
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/plans");
        raceCache = hasRace(data || []);
        if (alive) setRace(raceCache);
      } catch { /* silent — nav degrades to no Race tab */ }
    })();
    return () => { alive = false; };
  }, [plans]);

  const items = [
    { to: "/app", label: "Plan", icon: ClipboardList, testid: "nav-plan" },
    { to: "/tracker", label: "Tracker", icon: CalendarDays, testid: "nav-tracker",
      badge: hasTracker ? null : "+ Insights" },
    ...(race ? [{ to: "/race", label: "Race", icon: Flag, testid: "nav-race" }] : []),
    ...(planTier !== "lite" ? [{ to: "/recipes", label: "Recipes", icon: ChefHat, testid: "nav-recipes" }] : []),
    { to: "/learn", label: "Guide", icon: BookOpen, testid: "nav-guide" },
    { to: "/insights", label: "Insights", icon: LineChart, testid: "nav-insights" },
    { to: "/ask-ai", label: "Ask AI", icon: Sparkles, testid: "nav-coach" },
    ...(isCoach ? [{ to: "/coach", label: "Athletes", icon: Users, testid: "nav-coach-athletes" }] : []),
  ];

  return (
    <>
    <div className="hidden sm:block border-t border-white/10 bg-[#0A0A0A]" data-testid="primary-nav">
      {/* Wraps onto a second row on narrow screens rather than scrolling —
          nothing is ever hidden off-screen (mobile feedback, June 2026). */}
      <div className="max-w-6xl mx-auto px-2 sm:px-6 flex flex-wrap items-stretch gap-x-1 gap-y-1 py-1 sm:py-0">
        <nav className="flex flex-wrap items-stretch gap-x-1 gap-y-1 sm:flex-nowrap" data-swipe-ignore>
          {items.map(({ to, label, icon: Icon, testid, badge }) => {
            const active = pathname === to || pathname.startsWith(`${to}/`);
            return (
              <button
                key={to} type="button" data-testid={testid} onClick={() => navigate(to)} title={label}
                className={`group relative inline-flex items-center gap-1.5 sm:gap-2 my-1 px-2.5 sm:px-4 h-8 sm:h-9 rounded-md border text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.1em] sm:tracking-[0.14em] transition-colors ${
                  active
                    ? "spc-nav-active text-[#E2E30A] border-[#E2E30A]/60 bg-[#E2E30A]/[0.09]"
                    : "text-zinc-400 border-transparent hover:text-[#E2E30A] hover:bg-[#E2E30A]/[0.05]"}`}
              >
                <Icon className="h-4 w-4 transition-transform group-hover:scale-110" strokeWidth={2.2} />
                <span>{label}</span>
                {badge && (
                  <span className="rounded-full bg-[#E2E30A] text-black text-[8px] font-bold leading-none px-1.5 py-0.5 tracking-wide">{badge}</span>
                )}
              </button>
            );
          })}
        </nav>
        {right && <div className="ml-auto self-center py-1">{right}</div>}
      </div>
    </div>
    <BottomNav hasRace={!!race} />
    </>
  );
}
